import { notifyError } from "@/lib/notify-error";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import usePresence from "@convex-dev/presence/react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { api } from "@constructionautomation/backend";
import { useUserProfile } from "@/components/auth";

import { ViewerBackButton, ViewerTopBar } from "@/components/layout";
import { useViewerExit } from "@/components/layout/use-viewer-exit";
import { ViewerMetaChip } from "@/components/semantic-badges";
import { Card, CardContent } from "@/components/ui/card";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import {
  DEFAULT_TOOL_COLORS,
  styleForTool,
  type Tool,
} from "@/features/rev-collab/rev-collab-session-helpers";
import { RevCollabThreadPanel, RevCollabThreadToggle } from "@/features/rev-collab/rev-collab-thread-panel";
import type {
  AnnotationRecord,
  AnnotationSnapshot,
  CollaboratorPresence,
  MentionableUser,
  PendingAnnotationDraft,
  SessionDetailsRecord,
  StatusFilter,
  ThreadCountRow,
  ThreadRecord,
  UndoAction,
  ViewerRecord,
  CommentRecord,
} from "@/features/rev-collab/rev-collab-types";
import { RevCollabViewportOverlays } from "@/features/rev-collab/rev-collab-viewport-overlays";
import { RevCollabViewportStage } from "@/features/rev-collab/rev-collab-viewport-stage";
import { buildDocumentDownloadFilename } from "@/lib/document-download";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";
import { ReadOnlyTakeoffLayer } from "@/features/takeoff-pilot/read-only-takeoff-layer";
import {
  getAnnotationCenter,
  getViewportContentCenter,
} from "@/features/rev-collab/rev-collab-viewport-utils";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

interface RevCollabSessionProps {
  revisionId: Id<"projectDocumentRevisions">;
}

interface RevCollabSessionInnerProps {
  revisionId: Id<"projectDocumentRevisions">;
  userId: string;
  roomId: string;
  mimeType?: string;
  fileUrl: string;
  filename: string;
  documentName: string;
  revisionLabel: string;
}

function useRevCollabSessionResources(args: {
  revisionId: Id<"projectDocumentRevisions">;
  activePage: number;
  selectedThreadId: Id<"revCollabThreads"> | null;
  statusFilter: StatusFilter;
  roomId: string;
  userId: string;
}) {
  const { revisionId, activePage, selectedThreadId, statusFilter, roomId, userId } = args;

  const annotationsQuery = useQuery(
    convexQuery(api.revCollab.listAnnotationsByPage, {
      revisionId,
      pageNumber: activePage,
    })
  );
  const threadCountsQuery = useQuery(
    convexQuery(api.revCollab.listThreadCountsByRevision, { revisionId })
  );
  const threadsQuery = useQuery(
    convexQuery(api.revCollab.listThreadsByPage, {
      revisionId,
      pageNumber: activePage,
      status: statusFilter === "all" ? undefined : statusFilter,
    })
  );
  const commentsQuery = useQuery(
    convexQuery(
      api.revCollab.listCommentsByThread,
      selectedThreadId ? { threadId: selectedThreadId } : "skip"
    )
  );
  const mentionableUsersQuery = useQuery(
    convexQuery(api.revCollab.listMentionableUsers, { revisionId })
  );
  const takeoffLayerQuery = useQuery(
    convexQuery(api.takeoffPilot.measurements.getReadOnlyLayerForRevisionPage, {
      revisionId,
      pageNumber: activePage,
    })
  );
  const presenceState = usePresence(api.revCollabPresence, roomId, userId, 10_000);

  const queryError =
    annotationsQuery.error ||
    threadCountsQuery.error ||
    threadsQuery.error ||
    commentsQuery.error ||
    mentionableUsersQuery.error ||
    takeoffLayerQuery.error;
  if (queryError) {
    throw queryError;
  }

  return {
    annotations: annotationsQuery.data as AnnotationRecord[] | undefined,
    threadCounts: threadCountsQuery.data as ThreadCountRow[] | undefined,
    threads: threadsQuery.data as ThreadRecord[] | undefined,
    comments: commentsQuery.data as CommentRecord[] | undefined,
    mentionableUsers: mentionableUsersQuery.data as MentionableUser[] | undefined,
    takeoffLayer: takeoffLayerQuery.data,
    presenceState,
    createAnnotationWithThreadAndComment: useMutation(
      api.revCollab.createAnnotationWithThreadAndComment
    ),
    updateAnnotationGeometry: useMutation(api.revCollab.updateAnnotationGeometry),
    replaceAnnotationState: useMutation(api.revCollab.replaceAnnotationState),
    archiveAnnotation: useMutation(api.revCollab.archiveAnnotation),
    createThread: useMutation(api.revCollab.createThread),
    setThreadStatus: useMutation(api.revCollab.setThreadStatus),
    createComment: useMutation(api.revCollab.createComment),
    deleteComment: useMutation(api.revCollab.deleteComment),
    deleteThread: useMutation(api.revCollab.deleteThread),
    markThreadRead: useMutation(api.notifications.markReadByThread),
  };
}

function useResponsiveThreadPanelState() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  const [threadPanelOpen, setThreadPanelOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  const [desktopThreadPanelMinimized, setDesktopThreadPanelMinimized] = useState(false);
  const previousMobileRef = useRef(isMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (previousMobileRef.current !== isMobile) {
      setThreadPanelOpen(!isMobile);
      setDesktopThreadPanelMinimized(false);
      previousMobileRef.current = isMobile;
    }
  }, [isMobile]);

  return {
    isMobile,
    threadPanelOpen,
    setThreadPanelOpen,
    desktopThreadPanelMinimized,
    setDesktopThreadPanelMinimized,
  };
}

/** Loading / error state that keeps the viewer's exit available. */
function RevCollabSessionStatus({ title, message }: { title: string; message: string }) {
  const exitViewer = useViewerExit({ fallback: { to: "/revCollab", title: "CollabPilot" } });
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <ViewerTopBar
        className="shrink-0"
        back={<ViewerBackButton label="Back to CollabPilot" onBack={exitViewer} />}
        title={title}
      />
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">{message}</CardContent>
        </Card>
      </div>
    </div>
  );
}

export function RevCollabSession({ revisionId }: RevCollabSessionProps) {
  const { userProfile } = useUserProfile();
  const sessionDetailsQuery = useQuery(
    convexQuery(api.revCollab.getRevisionSession, { revisionId })
  );
  const viewer = userProfile as ViewerRecord | undefined;
  const sessionDetails = sessionDetailsQuery.data as SessionDetailsRecord | undefined;
  const ensureSession = useMutation(api.revCollab.ensureSession);
  const stableRevisionRef = useRef<string>(String(revisionId));
  const [stableFileUrl, setStableFileUrl] = useState<string | null>(null);
  // Creating the session row is a write; a reviewer's view works without one, so viewing never
  // changes the document's Collab history.
  const canEdit = useCanEdit();
  useEffect(() => {
    if (!canEdit) return;
    void ensureSession({ revisionId }).catch((error) => {
      console.error(error);
    });
  }, [canEdit, ensureSession, revisionId]);

  useEffect(() => {
    const revisionKey = String(revisionId);
    if (stableRevisionRef.current !== revisionKey) {
      stableRevisionRef.current = revisionKey;
      setStableFileUrl(sessionDetails?.fileUrl ?? null);
      return;
    }

    if (sessionDetails?.fileUrl && sessionDetails.fileUrl !== stableFileUrl) {
      setStableFileUrl(sessionDetails.fileUrl);
    }
  }, [revisionId, sessionDetails?.fileUrl, stableFileUrl]);

  const fileUrl = stableFileUrl ?? sessionDetails?.fileUrl ?? null;

  if (viewer === undefined || sessionDetails === undefined) {
    return <RevCollabSessionStatus title="CollabPilot" message="Loading session..." />;
  }

  if (!viewer?.userId || !fileUrl) {
    return (
      <RevCollabSessionStatus
        title={sessionDetails?.document.name ?? "CollabPilot"}
        message="Unable to load this revision session."
      />
    );
  }

  return (
    <ActionLogProvider
      projectId={sessionDetails.project._id}
      pilot="CollabPilot"
      targetKind="projectDocumentRevision"
      targetId={revisionId}
    >
      <RevCollabSessionInner
        revisionId={revisionId}
        userId={String(viewer.userId)}
        roomId={sessionDetails.roomId}
        mimeType={sessionDetails.revision.mimeType}
        fileUrl={fileUrl}
        filename={buildDocumentDownloadFilename(
          sessionDetails.document.name,
          sessionDetails.revision.originalFilename,
          sessionDetails.revision.revisionNumber
        )}
        documentName={sessionDetails.document.name}
        revisionLabel={`${sessionDetails.revision.revisionName} (r${sessionDetails.revision.revisionNumber})`}
      />
    </ActionLogProvider>
  );
}

function RevCollabSessionInner({
  revisionId,
  userId,
  roomId,
  mimeType,
  fileUrl,
  filename,
  documentName,
  revisionLabel,
}: RevCollabSessionInnerProps) {
  const recordActionLog = useRecordActionLog();
  const exitViewer = useViewerExit({ fallback: { to: "/revCollab", title: "CollabPilot" } });
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [tool, setTool] = useState<Tool>("select");
  const [toolColors, setToolColors] = useState<Record<Exclude<Tool, "select" | "hand">, string>>(
    DEFAULT_TOOL_COLORS
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [selectedAnnotationId, setSelectedAnnotationId] =
    useState<Id<"revCollabAnnotations"> | null>(null);
  const [selectedThreadId, setSelectedThreadId] =
    useState<Id<"revCollabThreads"> | null>(null);
  const [pendingAnnotationDraft, setPendingAnnotationDraft] =
    useState<PendingAnnotationDraft | null>(null);
  const [pendingCommentBody, setPendingCommentBody] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const [selectedMentions, setSelectedMentions] = useState<Set<string>>(new Set());
  const [showTakeoffLayer, setShowTakeoffLayer] = useState(true);
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoAction[]>([]);
  const {
    isMobile,
    threadPanelOpen,
    setThreadPanelOpen,
    desktopThreadPanelMinimized,
    setDesktopThreadPanelMinimized,
  } = useResponsiveThreadPanelState();

  const canEdit = useCanEdit();
  const canEditShapes = !isMobile && canEdit;
  const expandedThreadPanelWidth =
    !isMobile && threadPanelOpen && !desktopThreadPanelMinimized ? 380 : 0;
  const viewportApiRef = useRef<DocumentViewportValue | null>(null);

  const {
    annotations,
    threadCounts,
    threads,
    comments,
    mentionableUsers,
    takeoffLayer,
    presenceState,
    createAnnotationWithThreadAndComment,
    updateAnnotationGeometry,
    replaceAnnotationState,
    archiveAnnotation,
    createThread,
    setThreadStatus,
    createComment,
    deleteComment,
    deleteThread,
    markThreadRead,
  } = useRevCollabSessionResources({
    revisionId,
    activePage,
    selectedThreadId,
    statusFilter,
    roomId,
    userId,
  });

  const resetCommentComposer = useCallback(() => {
    setCommentBody("");
    setSelectedMentions(new Set());
  }, []);

  const clearPendingDraft = useCallback(() => {
    setPendingAnnotationDraft(null);
    setPendingCommentBody("");
  }, []);

  const resetPendingCommentBody = useCallback(() => {
    setPendingCommentBody("");
  }, []);

  // A member demoted mid-session drops any drawing tool or unsaved annotation draft.
  useOnEditAccessLost(() => {
    clearPendingDraft();
    setTool("select");
  });

  useEffect(() => {
    if (!selectedThreadId || threads === undefined) {
      return;
    }

    if (!threads.some((thread) => thread._id === selectedThreadId)) {
      setSelectedThreadId(null);
      resetCommentComposer();
    }
  }, [resetCommentComposer, selectedThreadId, threads]);

  useEffect(() => {
    resetCommentComposer();
  }, [resetCommentComposer, selectedThreadId]);

  useEffect(() => {
    setSelectedThreadId(null);
    resetCommentComposer();
    setSelectedAnnotationId(null);
    clearPendingDraft();
  }, [activePage, clearPendingDraft, resetCommentComposer]);

  const annotationsById = useMemo(() => {
    const map = new Map<string, AnnotationRecord>();
    (annotations ?? []).forEach((annotation) => {
      map.set(String(annotation._id), annotation);
    });
    return map;
  }, [annotations]);

  const threadCountMap = useMemo(() => {
    const map = new Map<number, { open: number; resolved: number }>();
    (threadCounts ?? []).forEach((row) => {
      map.set(row.pageNumber, { open: row.open, resolved: row.resolved });
    });
    return map;
  }, [threadCounts]);

  const collaborators = useMemo(
    () =>
      ((presenceState ?? []) as Array<{ userId: string; name?: string; data?: unknown }>)
        .filter((entry) => entry.userId !== userId)
        .map(
          (entry): CollaboratorPresence => ({
            userId: entry.userId,
            name: entry.name,
            data: (entry.data ?? {}) as CollaboratorPresence["data"],
          })
        ),
    [presenceState, userId]
  );

  const selectedAnnotation = selectedAnnotationId
    ? annotationsById.get(String(selectedAnnotationId)) ?? null
    : null;
  const onlineCount = Math.max(1, presenceState?.length ?? 0);
  const currentPageCounts = threadCountMap.get(activePage) ?? { open: 0, resolved: 0 };
  const takeoffLayerAvailable = Boolean(
    takeoffLayer && (takeoffLayer.detections.length > 0 || takeoffLayer.measurements.length > 0)
  );

  const source = useMemo(
    () => ({
      revisionId: String(revisionId),
      fileUrl,
      filename,
      mimeType,
      mediaKind: getDocumentMediaKind(filename, mimeType),
    }),
    [fileUrl, filename, mimeType, revisionId]
  );

  const pushUndoAction = useCallback((action: UndoAction) => {
    setUndoStack((current) => [...current, action]);
    setRedoStack([]);
  }, []);

  const safeRecordActionLog = useCallback(async () => {
    try {
      await recordActionLog();
    } catch (error) {
      console.warn("Action log failed (non-fatal):", error);
    }
  }, [recordActionLog]);

  const applySnapshot = useCallback(
    async (snapshot: AnnotationSnapshot) => {
      if (snapshot.archivedAt !== null) {
        await archiveAnnotation({ annotationId: snapshot.annotationId });
        await safeRecordActionLog();
        return;
      }

      await replaceAnnotationState({
        annotationId: snapshot.annotationId,
        geometry: snapshot.geometry,
        style: snapshot.style,
        archivedAt: null,
      });
      await safeRecordActionLog();
    },
    [archiveAnnotation, replaceAnnotationState, safeRecordActionLog]
  );

  const handleUndo = useCallback(async () => {
    const action = undoStack[undoStack.length - 1];
    if (!action) {
      return;
    }

    try {
      await applySnapshot(action.before);
      setUndoStack((current) => current.slice(0, -1));
      setRedoStack((current) => [...current, action]);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to undo");
    }
  }, [applySnapshot, undoStack]);

  const handleRedo = useCallback(async () => {
    const action = redoStack[redoStack.length - 1];
    if (!action) {
      return;
    }

    try {
      await applySnapshot(action.after);
      setRedoStack((current) => current.slice(0, -1));
      setUndoStack((current) => [...current, action]);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to redo");
    }
  }, [applySnapshot, redoStack]);

  const handleSavePendingAnnotation = useCallback(async () => {
    if (!pendingAnnotationDraft) {
      return;
    }

    const comment = pendingCommentBody.trim();
    if (!comment) {
      return;
    }

    try {
      const style = styleForTool(
        pendingAnnotationDraft.kind,
        toolColors[pendingAnnotationDraft.kind]
      );
      const { annotationId, threadId } = await createAnnotationWithThreadAndComment({
        revisionId,
        pageNumber: activePage,
        kind: pendingAnnotationDraft.kind,
        geometry: pendingAnnotationDraft.geometry,
        style,
        initialCommentBody: comment,
        mentionUserIds: [],
      });
      await safeRecordActionLog();

      try {
        await markThreadRead({ threadId });
      } catch (error) {
        console.error("Failed to mark thread read (non-fatal):", error);
      }

      const archivedAt = Date.now();
      pushUndoAction({
        before: {
          annotationId,
          geometry: pendingAnnotationDraft.geometry,
          style,
          archivedAt,
        },
        after: {
          annotationId,
          geometry: pendingAnnotationDraft.geometry,
          style,
          archivedAt: null,
        },
      });

      setSelectedAnnotationId(annotationId);
      setSelectedThreadId(threadId);
      clearPendingDraft();
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to save annotation and comment");
    }
  }, [
    activePage,
    clearPendingDraft,
    createAnnotationWithThreadAndComment,
    markThreadRead,
    pendingAnnotationDraft,
    pendingCommentBody,
    pushUndoAction,
    revisionId,
    safeRecordActionLog,
    toolColors,
  ]);

  const handleArchiveSelected = useCallback(async () => {
    if (!selectedAnnotation) {
      return;
    }

    const before: AnnotationSnapshot = {
      annotationId: selectedAnnotation._id,
      geometry: selectedAnnotation.geometry,
      style: selectedAnnotation.style,
      archivedAt: selectedAnnotation.archivedAt ?? null,
    };
    const archivedAt = Date.now();

    try {
      await archiveAnnotation({ annotationId: selectedAnnotation._id });
      await safeRecordActionLog();
      pushUndoAction({
        before,
        after: {
          annotationId: selectedAnnotation._id,
          geometry: selectedAnnotation.geometry,
          style: selectedAnnotation.style,
          archivedAt,
        },
      });
      setSelectedAnnotationId(null);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to delete annotation");
    }
  }, [archiveAnnotation, pushUndoAction, safeRecordActionLog, selectedAnnotation]);

  const handleCreatePageThread = useCallback(async (): Promise<Id<"revCollabThreads"> | null> => {
    try {
      const threadId = await createThread({
        revisionId,
        pageNumber: activePage,
        anchorType: "page",
      });
      setSelectedThreadId(threadId);
      await markThreadRead({ threadId });
      return threadId;
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to create thread");
      return null;
    }
  }, [activePage, createThread, markThreadRead, revisionId]);

  const handleCreatePageComment = useCallback(
    async (body: string, mentionIds: string[]) => {
      const threadId = await handleCreatePageThread();
      if (!threadId) {
        throw new Error("Failed to create thread");
      }

      try {
        await createComment({
          threadId,
          body,
          mentionUserIds: mentionIds as Id<"users">[],
        });
        await safeRecordActionLog();
      } catch (error) {
        await deleteThread({ threadId });
        throw error;
      }
    },
    [createComment, deleteThread, handleCreatePageThread, safeRecordActionLog]
  );

  const handleSelectThread = useCallback(
    async (threadId: Id<"revCollabThreads">, annotationId?: Id<"revCollabAnnotations"> | null) => {
      setSelectedThreadId(threadId);
      setSelectedAnnotationId(annotationId ?? null);
      try {
        await markThreadRead({ threadId });
      } catch (error) {
        console.error(error);
      }
    },
    [markThreadRead]
  );

  const handleZoomToAnnotation = useCallback(
    (annotationId: Id<"revCollabAnnotations">) => {
      const annotation = annotationsById.get(String(annotationId));
      const viewport = viewportApiRef.current;
      if (!annotation || !viewport) {
        return;
      }

      const center = getAnnotationCenter(annotation);
      const pageBounds = viewport.transforms.pageRectToScreenRect({
        x: 0,
        y: 0,
        width: 1,
        height: 1,
      });
      const contentCenter = getViewportContentCenter(viewport);
      const targetZoom = 2.5;
      const baseWidth = pageBounds.width / viewport.camera.zoom;
      const baseHeight = pageBounds.height / viewport.camera.zoom;

      viewport.requestZoomAt(targetZoom, contentCenter);
      viewport.setPan({
        x: baseWidth * targetZoom * (0.5 - center.x),
        y: baseHeight * targetZoom * (0.5 - center.y),
      });
    },
    [annotationsById]
  );

  const handleSubmitComment = useCallback(async () => {
    if (!selectedThreadId) {
      return;
    }

    try {
      await createComment({
        threadId: selectedThreadId,
        body: commentBody,
        mentionUserIds: Array.from(selectedMentions) as Id<"users">[],
      });
      await safeRecordActionLog();
      setCommentBody("");
      setSelectedMentions(new Set());
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to post comment");
    }
  }, [commentBody, createComment, safeRecordActionLog, selectedMentions, selectedThreadId]);

  const handleAnnotationDragEnd = useCallback(
    async (annotationId: Id<"revCollabAnnotations">, x: number, y: number) => {
      const annotation = annotationsById.get(String(annotationId));
      const viewport = viewportApiRef.current;
      if (!annotation || !viewport) {
        return;
      }

      const before: AnnotationSnapshot = {
        annotationId,
        geometry: annotation.geometry,
        style: annotation.style,
        archivedAt: annotation.archivedAt ?? null,
      };
      const nextPoint = viewport.transforms.screenToPagePoint({ x, y });
      if (!nextPoint) {
        return;
      }

      const nextGeometry = {
        ...annotation.geometry,
        x: nextPoint.x,
        y: nextPoint.y,
      };

      await updateAnnotationGeometry({
        annotationId,
        geometry: nextGeometry,
        style: annotation.style,
      });
      await safeRecordActionLog();
      pushUndoAction({
        before,
        after: {
          annotationId,
          geometry: nextGeometry,
          style: annotation.style,
          archivedAt: annotation.archivedAt ?? null,
        },
      });
    },
    [annotationsById, pushUndoAction, safeRecordActionLog, updateAnnotationGeometry]
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <ViewerTopBar
        className="shrink-0"
        back={<ViewerBackButton label="Back to CollabPilot" onBack={exitViewer} />}
        title={documentName}
        subtitle={revisionLabel}
        readOnly={!canEdit}
        meta={
          <>
            <ViewerMetaChip className="hidden min-w-0 lg:inline-flex" title={filename}>
              <span className="truncate">{filename}</span>
            </ViewerMetaChip>
            <ViewerMetaChip className="hidden tabular-nums sm:inline-flex">
              Page {activePage} of {pageCount}
            </ViewerMetaChip>
            <ViewerMetaChip className="hidden tabular-nums md:inline-flex">
              {currentPageCounts.open} Open
            </ViewerMetaChip>
            <ViewerMetaChip className="hidden tabular-nums md:inline-flex">
              {onlineCount} Online
            </ViewerMetaChip>
          </>
        }
      />

      <div className="relative min-h-0 flex-1 overflow-hidden bg-muted/20">
        <DocumentViewportHost
          source={source}
          activePage={activePage}
          onActivePageChange={setActivePage}
          onPageCountChange={setPageCount}
          pointerOwner="external"
          enableWheelZoom
          viewportPadding={{
            top: 88,
            right: 24,
            bottom: canEditShapes ? 76 : 112,
            left: 24,
          }}
          className="absolute inset-0 overflow-hidden"
        >
          <RevCollabViewportStage
            activePage={activePage}
            tool={tool}
            setTool={setTool}
            toolColors={toolColors}
            canEditShapes={canEditShapes}
            annotations={annotations}
            collaborators={collaborators}
            userId={userId}
            selectedAnnotationId={selectedAnnotationId}
            setSelectedAnnotationId={setSelectedAnnotationId}
            pendingAnnotationDraft={pendingAnnotationDraft}
            setPendingAnnotationDraft={setPendingAnnotationDraft}
            clearPendingDraft={clearPendingDraft}
            resetPendingCommentBody={resetPendingCommentBody}
            viewportApiRef={viewportApiRef}
            onAnnotationDragEnd={handleAnnotationDragEnd}
          />
          {showTakeoffLayer && takeoffLayerAvailable && takeoffLayer ? (
            <ReadOnlyTakeoffLayer layer={takeoffLayer} />
          ) : null}
          <RevCollabViewportOverlays
            tool={tool}
            setTool={setTool}
            toolColors={toolColors}
            setToolColors={setToolColors}
            canEditShapes={canEditShapes}
            activePage={activePage}
            pageCount={pageCount}
            takeoffLayerSummary={
              takeoffLayer
                ? {
                    jobId: takeoffLayer.jobId,
                    jobName: takeoffLayer.jobName,
                    detectionCount: takeoffLayer.detections.length,
                    measurementCount: takeoffLayer.measurementCount,
                  }
                : null
            }
            showTakeoffLayer={showTakeoffLayer}
            onShowTakeoffLayerChange={setShowTakeoffLayer}
            undoCount={undoStack.length}
            redoCount={redoStack.length}
            canArchiveSelected={
              !!selectedAnnotation && String(selectedAnnotation.createdByUserId) === userId
            }
            onUndo={() => void handleUndo()}
            onRedo={() => void handleRedo()}
            onArchiveSelected={() => void handleArchiveSelected()}
            pendingAnnotationDraft={pendingAnnotationDraft}
            pendingCommentBody={pendingCommentBody}
            setPendingCommentBody={setPendingCommentBody}
            onClearPendingDraft={clearPendingDraft}
            onSavePendingAnnotation={() => void handleSavePendingAnnotation()}
            expandedThreadPanelWidth={expandedThreadPanelWidth}
          />
        </DocumentViewportHost>

        {threadPanelOpen && (isMobile || !desktopThreadPanelMinimized) ? (
          <RevCollabThreadPanel
            isMobile={isMobile}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            threads={threads}
            selectedThreadId={selectedThreadId}
            comments={comments}
            userId={userId}
            mentionableUsers={mentionableUsers}
            commentBody={commentBody}
            setCommentBody={setCommentBody}
            selectedMentions={selectedMentions}
            setSelectedMentions={setSelectedMentions}
            onSelectThread={handleSelectThread}
            onSubmitComment={handleSubmitComment}
            onDeleteComment={deleteComment}
            onSetThreadStatus={setThreadStatus}
            onZoomToAnnotation={handleZoomToAnnotation}
            onClose={() => setThreadPanelOpen(false)}
            onMinimize={() => setDesktopThreadPanelMinimized(true)}
            onCreatePageComment={handleCreatePageComment}
          />
        ) : null}

        {!threadPanelOpen || (!isMobile && desktopThreadPanelMinimized) ? (
          <RevCollabThreadToggle
            threadCount={(threads ?? []).length}
            onOpen={() => {
              setThreadPanelOpen(true);
              setDesktopThreadPanelMinimized(false);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
